import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Botao, Cartao, CartaoTopo, Selo, Tabela, Vazio, Campo, Entrada, type Coluna, type TomDoSelo } from "@notes/ui";
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, ShieldBan, Search, X } from "lucide-react";
import { Pagina, Secao, Carregando, Erro } from "../shell/Shell.js";
import { Ramal } from "./Ramal.js";
import { trpc, usarVoz, usarLigacoes, mensagemDeErro } from "../lib/api.js";
import { dataHora, duracao } from "../lib/formato.js";

type LinhaDeLigacao = {
  id: string; direcao: "entrada" | "saida" | "interna"; status: string;
  deE164: string | null; paraE164: string | null;
  createdAt: Date | string; conversaS: number | null; esperaS: number | null;
  reuniaoId: string | null; negociacaoCrmId: string | null;
  causaEncerramento: string | null;
};

/** O estado da ligação em português, sem termo de central telefônica. */
export function EstadoDaLigacao({ status }: { status: string }) {
  const mapa: Record<string, { rotulo: string; tom: TomDoSelo }> = {
    fila: { rotulo: "Na fila", tom: "neutro" },
    tocando: { rotulo: "Tocando", tom: "atencao" },
    atendida: { rotulo: "Atendida", tom: "marca" },
    em_conversa: { rotulo: "Em conversa", tom: "marca" },
    encerrada: { rotulo: "Encerrada", tom: "bom" },
    falhou: { rotulo: "Não completou", tom: "critico" },
    abandonada: { rotulo: "Desligou esperando", tom: "critico" },
  };
  const m = mapa[status] ?? { rotulo: status, tom: "neutro" as TomDoSelo };
  return <Selo tom={m.tom}>{m.rotulo}</Selo>;
}

/** Por que a ligação terminou, dito para quem vende e não para quem opera central. */
export function motivoDoFim(causa: string | null): string {
  const mapa: Record<string, string> = {
    NORMAL_CLEARING: "conversa encerrada",
    USER_BUSY: "linha ocupada",
    NO_ANSWER: "ninguém atendeu",
    ORIGINATOR_CANCEL: "desistiu antes de atender",
    CALL_REJECTED: "recusou a chamada",
    UNALLOCATED_NUMBER: "número não existe",
    NORMAL_TEMPORARY_FAILURE: "falha na operadora",
  };
  return causa ? (mapa[causa] ?? "encerrada") : "sem registro";
}

const DIRECOES = [
  { chave: "todas", rotulo: "Todas" },
  { chave: "saida", rotulo: "Feitas" },
  { chave: "entrada", rotulo: "Recebidas" },
] as const;

/**
 * LIGAÇÕES. Mesma gramática da tela de reuniões, porque é a mesma pergunta de
 * comparação ("com quem eu falei ontem?"). A diferença que importa: daqui a
 * ligação leva para a REUNIÃO dela, que é onde está a transcrição, o resumo e
 * o áudio. A lista é o índice; a conversa mora no mesmo lugar de sempre.
 */
export function Ligacoes() {
  const voz = usarVoz();
  const q = usarLigacoes(true);
  const navegar = useNavigate();
  const qc = useQueryClient();
  const [busca, setBusca] = useState("");
  const [direcao, setDirecao] = useState<(typeof DIRECOES)[number]["chave"]>("todas");
  const [para, setPara] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);

  const ligar = useMutation({
    mutationFn: (numero: string) => trpc.voz.ligar.mutate({ para: numero }),
    onSuccess: () => {
      setPara("");
      setAviso(null);
      void qc.invalidateQueries({ queryKey: ["voz"] });
    },
    onError: (e) => setAviso(mensagemDeErro(e)),
  });

  const todas = (q.data?.ligacoes ?? []) as unknown as LinhaDeLigacao[];
  const filtradas = useMemo(() => {
    const termo = busca.replace(/\D/g, "");
    return todas.filter((l) => {
      if (direcao !== "todas" && l.direcao !== direcao) return false;
      if (termo && !`${l.deE164 ?? ""}${l.paraE164 ?? ""}`.includes(termo)) return false;
      return true;
    });
  }, [todas, busca, direcao]);

  if (voz.isLoading) return <Pagina titulo="Ligações"><Carregando tipo="tabela" /></Pagina>;

  // Agência sem telefonia não vê meia tela: vê o que precisa fazer para ter.
  if (voz.isError || !voz.data?.ativo) {
    return (
      <Pagina titulo="Ligações" largura="estreita">
        <Secao ordem={0}>
          <Vazio
            icone={<Phone size={22} />}
            titulo="Esta agência ainda não tem telefonia"
            descricao="Com o EnturOS Voice, a ligação é gravada, transcrita e entra na negociação junto com as reuniões. Fale com a Entur para ligar o módulo."
          />
        </Secao>
      </Pagina>
    );
  }

  const estado = voz.data;

  const colunas: Array<Coluna<LinhaDeLigacao>> = [
    {
      chave: "quem", cabecalho: "Quem", ordenarPor: (l) => l.paraE164 ?? l.deE164 ?? "",
      celula: (l) => (
        <span className="enotes-tb-titulo">
          <span className="enotes-tb-nome">
            {l.direcao === "entrada" ? <PhoneIncoming size={13} aria-hidden /> : <PhoneOutgoing size={13} aria-hidden />}
            {" "}
            {l.direcao === "entrada" ? l.deE164 : l.paraE164}
          </span>
          {l.negociacaoCrmId && <Selo tom="marca">negociação {l.negociacaoCrmId}</Selo>}
        </span>
      ),
    },
    {
      chave: "quando", cabecalho: "Quando", largura: "11rem",
      ordenarPor: (l) => new Date(l.createdAt).getTime(),
      celula: (l) => <span className="carimbo">{dataHora(l.createdAt)}</span>,
    },
    {
      chave: "conversa", cabecalho: "Conversa", largura: "7rem", alinhamento: "fim",
      ordenarPor: (l) => l.conversaS ?? 0,
      celula: (l) => <span className="num">{duracao(l.conversaS)}</span>,
    },
    {
      chave: "estado", cabecalho: "Estado", largura: "11rem",
      ordenarPor: (l) => l.status,
      celula: (l) => <EstadoDaLigacao status={l.status} />,
    },
    {
      chave: "fim", cabecalho: "Como terminou", largura: "12rem",
      ordenarPor: (l) => l.causaEncerramento ?? "",
      celula: (l) => <span className="carimbo">{motivoDoFim(l.causaEncerramento)}</span>,
    },
  ];

  return (
    <Pagina
      titulo="Ligações"
      largura="larga"
      descricao="Cada ligação vira uma conversa gravada, transcrita e ligada à negociação."
      acao={
        estado.numeroDeSaida
          ? <Selo tom="neutro">{estado.numeroDeSaida.e164}</Selo>
          : <Selo tom="atencao">sem número de saída</Selo>
      }
    >
      <Secao ordem={0}>
        <Cartao>
          <CartaoTopo
            icone={<Phone size={15} />}
            titulo="Ligar agora"
            descricao={estado.meuRamal
              ? "O aviso de gravação toca antes de a conversa começar, nos dois lados."
              : "Você ainda não tem um ramal nesta agência."}
            acao={<Ramal ativo={Boolean(estado.meuRamal)} />}
          />
          <form
            className="enotes-discador"
            onSubmit={(e) => { e.preventDefault(); if (para.trim()) ligar.mutate(para.trim()); }}
          >
            <Campo rotulo="Número" htmlFor="voz-para">
              <Entrada
                id="voz-para" value={para} onChange={(e) => setPara(e.target.value)}
                placeholder="(11) 98765-4321" inputMode="tel" autoComplete="off"
                disabled={!estado.meuRamal || !estado.numeroDeSaida}
              />
            </Campo>
            <Botao
              type="submit" variante="primario"
              ocupado={ligar.isPending}
              disabled={!para.trim() || !estado.meuRamal || !estado.numeroDeSaida}
            >
              <Phone size={14} />Ligar
            </Botao>
          </form>
          {aviso && <Erro mensagem={aviso} />}
          {estado.tetoMinutosDia != null && (
            <p className="enotes-dica-linha">
              <span className="num">{estado.minutosHoje}</span> de{" "}
              <span className="num">{estado.tetoMinutosDia}</span> minutos usados hoje.
            </p>
          )}
        </Cartao>
      </Secao>

      <Secao ordem={1}>
        <div className="enotes-filtros">
          <div className="enotes-busca-campo">
            <Search size={14} aria-hidden />
            <input
              className="enotes-busca-entrada" value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por número" aria-label="Buscar por número"
            />
            {busca && (
              <button className="enotes-icone" onClick={() => setBusca("")} aria-label="Limpar a busca"><X size={13} /></button>
            )}
          </div>
          <div className="enotes-chips" role="group" aria-label="Direção">
            {DIRECOES.map((d) => (
              <button key={d.chave} className="enotes-chip" data-ativo={direcao === d.chave || undefined}
                onClick={() => setDirecao(d.chave)}>{d.rotulo}</button>
            ))}
          </div>
        </div>
      </Secao>

      <Secao ordem={2}>
        {q.isError ? (
          <Erro mensagem={mensagemDeErro(q.error)} aoTentarDeNovo={() => void q.refetch()} />
        ) : (
          <Tabela
            colunas={colunas}
            linhas={filtradas}
            chave={(l) => l.id}
            // Clicar leva para a REUNIÃO, que é onde está a conversa. Ligação
            // ainda sem reunião não navega para lugar nenhum, em vez de abrir
            // uma tela vazia.
            aoClicar={(l) => { if (l.reuniaoId) void navegar({ to: "/reunioes/$id", params: { id: l.reuniaoId } }); }}
            vazio={
              todas.length === 0 ? (
                <Vazio
                  icone={<PhoneMissed size={22} />}
                  titulo="Nenhuma ligação ainda"
                  descricao="Assim que a primeira ligação acontecer, ela aparece aqui e a conversa entra na negociação."
                />
              ) : (
                <Vazio titulo="Nada com esses filtros" descricao="Tente outro período ou limpe a busca." />
              )
            }
          />
        )}
      </Secao>

      <Secao ordem={3}>
        <p className="enotes-dica-linha">
          <ShieldBan size={13} aria-hidden />{" "}
          Quem pedir para não ser chamado entra na lista de bloqueio e não recebe mais ligação desta agência.
        </p>
      </Secao>
    </Pagina>
  );
}
